import axios from "axios";

var STORAGE_KEY = "fx_rates_v1";
var LOCK_KEY = "fx_rates_lock_v1";
var CHANNEL_NAME = "fx_rates_channel_v1";

var REFRESH_HOUR = 15;
var REFRESH_MIN = 30;

// ----------------- utils -----------------
function safeParse(s) {
    try {
        return JSON.parse(s);
    } catch (e) {
        return null;
    }
}

function readCache() {
    return safeParse(localStorage.getItem(STORAGE_KEY) || "null");
}

function writeCache(obj) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
}

function trNowParts() {
    var dtf = new Intl.DateTimeFormat("tr-TR", {
        timeZone: "Europe/Istanbul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

    var partsArr = dtf.formatToParts(new Date());
    var p = {};
    for (var i = 0; i < partsArr.length; i++) {
        var x = partsArr[i];
        if (x.type !== "literal") p[x.type] = x.value;
    }

    return {
        ymd: p.year + "-" + p.month + "-" + p.day,
        h: parseInt(p.hour, 10),
        m: parseInt(p.minute, 10),
        s: parseInt(p.second, 10),
        iso: p.year + "-" + p.month + "-" + p.day + "T" + p.hour + ":" + p.minute + ":" + p.second + "+03:00",
    };
}

function after1530TR(now) {
    return now.h > REFRESH_HOUR || (now.h === REFRESH_HOUR && now.m >= REFRESH_MIN);
}

function today1530TRString(now) {
    return now.ymd + "T15:30:00+03:00";
}

function needRefresh(cache, now) {
    // cache yoksa -> refresh
    if (!cache) return true;

    // meta güvenliği
    if (!cache.meta || !cache.meta.asOfDate || !cache.meta.fetchedAt) return true;

    // başka gün -> refresh
    if (cache.meta.asOfDate !== now.ymd) return true;

    // aynı gün ve 15:30 öncesi -> refresh gerekmez
    if (!after1530TR(now)) return false;

    // 15:30 sonrası ve 15:30 sonrası fetch yapılmamış -> refresh
    return cache.meta.fetchedAt < today1530TRString(now);
}

function acquireLock(ttlMs) {
    var ttl = typeof ttlMs === "number" ? ttlMs : 15000;
    var now = Date.now();

    var cur = safeParse(localStorage.getItem(LOCK_KEY) || "null");
    if (cur && cur.until && cur.until > now) return false;

    localStorage.setItem(LOCK_KEY, JSON.stringify({ until: now + ttl }));
    return true;
}

function releaseLock() {
    localStorage.removeItem(LOCK_KEY);
}

function normalize(apiJson) {
    var out = {};
    var src = apiJson || {};

    Object.keys(src).forEach(function (ccy) {
        var row = src[ccy];
        var selling = row && row.Selling;

        if (typeof selling === "number") out[ccy] = selling;
    });

    return out;
}

// ----------------- channel (optional) -----------------
function createChannel() {
    try {
        if (typeof window !== "undefined" && "BroadcastChannel" in window) {
            return new BroadcastChannel(CHANNEL_NAME);
        }
    } catch (e) { }
    return null;
}

var bc = createChannel();

// ----------------- service -----------------
export function createFxService(options) {
    if (!options || !options.endpointUrl) throw new Error("endpointUrl required");
    var endpointUrl = options.endpointUrl;

    var listeners = [];
    var timer = null;

    function emit(payload) {
        for (var i = 0; i < listeners.length; i++) {
            try {
                listeners[i](payload);
            } catch (e) { }
        }
    }

    function subscribe(fn) {
        if (typeof fn !== "function") return function () { };

        listeners.push(fn);

        // anında cache bas
        var c = readCache();
        if (c) fn(c);

        return function unsubscribe() {
            var idx = listeners.indexOf(fn);
            if (idx >= 0) listeners.splice(idx, 1);
        };
    }

    // diğer sekmeden update geldiğinde
    if (bc) {
        bc.onmessage = function (ev) {
            if (ev && ev.data && ev.data.type === "FX_UPDATED" && ev.data.payload) {
                emit(ev.data.payload);
            }
        };
    }

    // BroadcastChannel yoksa storage event ile yakala
    window.addEventListener("storage", function (e) {
        if (e && e.key === STORAGE_KEY && e.newValue) {
            var payload = safeParse(e.newValue);
            if (payload) emit(payload);
        }
    });

    function broadcast(payload) {
        // diğer tab’lere haber
        try {
            if (bc) bc.postMessage({ type: "FX_UPDATED", payload: payload });
        } catch (e) { }
        // storage zaten writeCache ile tetiklenir
    }

    async function refresh(opts) {
        var silent = opts && typeof opts.silent === "boolean" ? opts.silent : true;
        var now = trNowParts();

        // offline -> cache varsa dön
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            var offCache = readCache();
            if (offCache) return offCache;
            var errOff = new Error("OFFLINE_NO_CACHE");
            if (!silent) throw errOff;
            return null;
        }

        // lock
        var locked = acquireLock(15000);
        if (!locked) {
            var c = readCache();
            if (c) return c;
            // cache yoksa devam (çok nadir)
        }

        try {
            var resp = await axios.get(endpointUrl, { timeout: 15000 });
            var rates = normalize(resp && resp.data);

            if (!rates || Object.keys(rates).length === 0) {
                throw new Error("FX_EMPTY_RESPONSE");
            }

            var payload = {
                v: 1,
                provider: "tcmb-via-my-api",
                rates: rates,
                meta: {
                    asOfDate: now.ymd,
                    fetchedAt: now.iso,
                },
            };

            writeCache(payload);
            broadcast(payload);
            emit(payload);

            return payload;
        } finally {
            releaseLock();
        }
    }

    // cache varsa hemen döner, gerekiyorsa arka planda refresh eder
    async function get(opts) {
        var force = opts && typeof opts.force === "boolean" ? opts.force : false;

        var now = trNowParts();
        var cache = readCache();

        if (cache && cache.rates) {
            var must = force ? true : needRefresh(cache, now);
            if (must) {
                refresh({ silent: true }).catch(function () { });
            }
            return cache;
        }

        // cache yoksa: blocking
        return await refresh({ silent: false });
    }

    // 15:30 scheduler (interval yok)
    function schedule1530() {
        if (timer) clearTimeout(timer);

        var n = trNowParts();
        var nowSec = n.h * 3600 + n.m * 60 + n.s;
        var targetSec = 15 * 3600 + 30 * 60;

        var delaySec = targetSec - nowSec;
        if (delaySec <= 0) delaySec += 24 * 3600;

        timer = setTimeout(function () {
            get({ force: false }).catch(function () { });
            schedule1530(); // yarın tekrar kur
        }, delaySec * 1000);
    }

    function attachLifecycle() {
        // görünür olunca kontrol (uzun açık kalan tab için ideal)
        document.addEventListener("visibilitychange", function () {
            if (document.visibilityState === "visible") {
                get({ force: false }).catch(function () { });
            }
        });

        // internet geri geldiğinde kontrol
        window.addEventListener("online", function () {
            get({ force: false }).catch(function () { });
        });

        // ilk kurulumda 15:30 zamanlayıcı
        schedule1530();
    }

    function destroy() {
        if (timer) clearTimeout(timer);
        timer = null;
        listeners = [];
        try {
            if (bc) bc.close();
        } catch (e) { }
    }

    return {
        read: readCache,
        subscribe: subscribe,
        get: get,
        refresh: refresh,
        attachLifecycle: attachLifecycle,
        schedule1530: schedule1530,
        destroy: destroy,
    };
}



// WEBPACK FOOTER //
// ./src/services/fxService.js